import React, { ReactNode, useMemo } from 'react';

import { rem, Box, Button, Flex, PopoverBody, useDisclosure } from '@vimeo/bokeh';
import { ChevronDown } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { BokehPopover } from '@vimeo/live-core/components/bokeh';
import {
  trackSortByMostPopular,
  trackSortByMostRecent,
} from '@vimeo/live-core/tracking/tracking_engagement_qna';
import {
  EQnaTab,
  EQuestionSortType,
  EQuestionsSortBy,
  ESortOrder,
  IQnASortingRule,
  ITabSorting,
} from '@vimeo/live-core/types/interaction_tools';

import { createInteractionDomName } from '../../../../libs/shared/utils/dom/identifying';
import { translations } from '../../../../translations';

interface IQuestionsSortingProps extends IBaseDomElementProps {
  tabId: EQnaTab;
  sorting: IQnASortingRule;
  changeSorting: (tabSorting: ITabSorting) => void;
}

export function QuestionsListSorting({
  id = createInteractionDomName('qna-list-sorting'),
  className = createInteractionDomName('qna-list-sorting'),
  tabId,
  sorting,
  changeSorting,
}: IQuestionsSortingProps) {
  const { isOpen: isMenuOpen, onClose: onCloseMenu, onToggle: onToggleMenu } = useDisclosure();

  const sortingLabel: string = {
    [EQuestionSortType.NEWEST]: translations.newest,
    [EQuestionSortType.OLDEST]: translations.oldest,
    [EQuestionSortType.MOST_POPULAR]: translations.mostPopular,
  }[sorting.type];

  const menuItems = useMemo(() => {
    const items = [
      {
        type: EQuestionSortType.NEWEST,
        order: ESortOrder.DESCENDING,
        copy: translations.newest,
        field:
          tabId === EQnaTab.ARCHIVED ? EQuestionsSortBy.ARCHIVED_TIME : EQuestionsSortBy.TIMESTAMP,
      },
      {
        type: EQuestionSortType.OLDEST,
        order: ESortOrder.ASCENDING,
        copy: translations.oldest,
        field:
          tabId === EQnaTab.ARCHIVED ? EQuestionsSortBy.ARCHIVED_TIME : EQuestionsSortBy.TIMESTAMP,
      },
    ];

    if (tabId !== EQnaTab.PENDING) {
      items.push({
        type: EQuestionSortType.MOST_POPULAR,
        order: ESortOrder.DESCENDING,
        copy: translations.mostPopular,
        field: EQuestionsSortBy.VOTES,
      });
    }

    return items;
  }, [tabId]);

  const sortingListContent: ReactNode = (
    <Box id={createInteractionDomName(id, 'list')} minWidth={rem(160)}>
      <PopoverBody>
        {menuItems.map(({ copy, field, type, order }) => (
          <Flex
            key={type}
            className={createInteractionDomName(id, 'list-item')}
            as={'span'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'start'}
            position={'relative'}
            cursor={'pointer'}
            width={'100%'}
            fontSize={'text-sm'}
            color={'text-primary'}
            borderRadius={'sm'}
            _hover={{
              backgroundColor: 'fill-component-hover',
            }}
            padding={`${rem(8)} ${rem(16)}`}
            onClick={() => {
              changeSorting({ [tabId]: { field, type, order } });
              onCloseMenu();

              // Track sorting type of qna.
              switch (type) {
                case EQuestionSortType.MOST_POPULAR:
                  trackSortByMostPopular();
                  break;

                case EQuestionSortType.NEWEST:
                  trackSortByMostRecent();
                  break;
              }
            }}
          >
            {copy}
          </Flex>
        ))}
      </PopoverBody>
    </Box>
  );

  return (
    <Flex
      id={id}
      className={className}
      alignItems={'center'}
      justifyContent={'flex-end'}
      gap={rem(8)}
    >
      <BokehPopover
        isOpen={isMenuOpen}
        inPortal={true}
        placement={'bottom-end'}
        gutter={8}
        triggerContent={
          <Button
            id={createInteractionDomName(id, 'button')}
            className={createInteractionDomName(className, 'button')}
            size={'xs'}
            variant={'minimal'}
            rightIcon={<ChevronDown />}
            onClick={onToggleMenu}
          >
            {sortingLabel}
          </Button>
        }
        content={sortingListContent}
        onClose={onCloseMenu}
      />
    </Flex>
  );
}
